import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router";
import { useAtomValue, useSetAtom } from "jotai";
import {
  Orbit,
  SlidersHorizontal,
  MessageCircle,
  Globe,
  LogOut,
  ChevronsUpDown,
} from "lucide-react";
import { authAtom, logoutAction } from "../stores/auth";
import { selectedAccountAtom } from "../stores/galaxy";
import { localeAtom, setLocaleAction } from "../stores/locale";
import { planApi } from "../api/client";
import { useT } from "../lib/i18n";
import ThemeToggle from "./ThemeToggle";

// AccountMenu is the identity row at the bottom of the sidebar chrome — the
// GitHub-style avatar popover (account-level / galaxy-crossing surfaces:
// Career, Control Room, Feedback, sign out) plus the JP/EN language toggle and
// the light/dark theme toggle. It's extracted from Sidebar so BOTH the
// operational Sidebar and the ArchiveLayout aside render the same bottom-left
// account menu — a departed/archived galaxy's frozen record view keeps it
// instead of dropping the identity controls.
//
// It owns its own state/atoms/effects (open-close + outside-click, the
// signed-in user, locale, the personal-galaxy Pro mark) so the caller only
// drops it in — no props are threaded through. currentAccount is derived the
// same way Sidebar does (URL is the source of truth; falls back to the
// selected galaxy / the user's own login when the route has no :account, e.g.
// the archive route). Career is user-scoped (/me/* APIs, JWT-keyed) regardless
// of which galaxy frames it, so the Galaxy Boundary holds at the data layer.
export function AccountMenu() {
  const { account: urlAccount } = useParams<{ account: string }>();
  const selectedAccount = useAtomValue(selectedAccountAtom);
  const auth = useAtomValue(authAtom);
  const currentAccount = urlAccount || selectedAccount || auth.user?.login || "";
  const logout = useSetAtom(logoutAction);
  const locale = useAtomValue(localeAtom);
  const setLocale = useSetAtom(setLocaleAction);
  const t = useT(locale);

  // Plan of the signed-in user's own personal galaxy (account_login === login).
  // Local UI status only, so kept in useState rather than an atom (manifest §1).
  // Reads planApi (the entitlement projection = account.plan), the SAME source
  // the switcher badge / limits use — NOT billingApi (the Stripe subscription)
  // — so this mark always matches entitlement and a dev plan override reflects.
  const personalLogin = auth.user?.login;
  const [personalPlan, setPersonalPlan] = useState<string | null>(null);

  useEffect(() => {
    if (!personalLogin) return;
    const ac = new AbortController();
    planApi
      .get(personalLogin)
      .then((info) => {
        if (!ac.signal.aborted) setPersonalPlan(info.plan);
      })
      .catch((e) => {
        // Silent: just don't render the badge. console.error so datadog catches it.
        if (!ac.signal.aborted) console.error("account menu plan fetch failed", e);
      });
    return () => ac.abort();
  }, [personalLogin]);

  const isPersonalPro = personalPlan === "pro";

  // Account menu (GitHub-style): the identity row opens a popover with the
  // account-level / galaxy-crossing surfaces (Control Room, Feedback, sign out).
  // Local open/close state per manifest §1; close on outside click.
  const [accountMenuOpen, setAccountMenuOpen] = useState(false);
  const accountMenuRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      if (accountMenuRef.current && !accountMenuRef.current.contains(e.target as Node)) {
        setAccountMenuOpen(false);
      }
    };
    if (accountMenuOpen) document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [accountMenuOpen]);

  const handleLogout = () => {
    logout();
    window.location.href = "/login";
  };

  return (
    <div
      className="border-t p-md"
      style={{ borderColor: "var(--border)" }}
    >
      {auth.user && (
        <div ref={accountMenuRef} className="relative mb-md">
          <button
            onClick={() => setAccountMenuOpen((v) => !v)}
            className="flex w-full items-center gap-md rounded-lg px-md py-md text-left transition-colors hover:glass-subtle"
            aria-haspopup="true"
            aria-expanded={accountMenuOpen}
          >
            {auth.user.avatarUrl ? (
              <img
                src={auth.user.avatarUrl}
                alt=""
                className="h-8 w-8 rounded-full"
                style={{
                  outline: isPersonalPro
                    ? "1.5px solid var(--gold)"
                    : "1px solid var(--border)",
                }}
              />
            ) : (
              <div
                className="flex h-8 w-8 items-center justify-center rounded-full text-xs"
                style={{
                  backgroundColor: "var(--border)",
                  color: "var(--text-strong)",
                  outline: isPersonalPro ? "1.5px solid var(--gold)" : undefined,
                }}
              >
                {auth.user.login.charAt(0).toUpperCase()}
              </div>
            )}
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-md">
                <p
                  className="truncate text-sm"
                  style={{ color: "var(--text-strong)", fontWeight: 500 }}
                >
                  {auth.user.login}
                </p>
                {/* Personal-galaxy Pro mark — gold (payment color). Payment status,
                    not an archetype identity, so kept restrained (no streak/anim). */}
                {isPersonalPro && (
                  <span
                    className="flex-shrink-0 rounded px-sm py-xs text-[10px] uppercase"
                    style={{
                      fontFamily: "var(--mono)",
                      fontWeight: 500,
                      letterSpacing: "0.08em",
                      color: "var(--gold)",
                      backgroundColor: "var(--gold-glow)",
                    }}
                  >
                    Pro
                  </span>
                )}
              </div>
              {auth.user.isStaff && (
                <p
                  className="text-[10px] uppercase"
                  style={{
                    fontFamily: "var(--mono)",
                    letterSpacing: "0.1em",
                    color: "var(--text-dim)",
                  }}
                >
                  OrbitLens 運営
                </p>
              )}
            </div>
            <ChevronsUpDown className="h-4 w-4 flex-shrink-0" style={{ color: "var(--text-dim)" }} />
          </button>

          {accountMenuOpen && (
            <div
              className="absolute bottom-full left-0 right-0 z-50 mb-md overflow-hidden rounded-xl shadow-2xl"
              style={{ background: "var(--bg-card-hi)", border: "1px solid var(--border)" }}
            >
              {/* Career is user-scoped (/me/career, its own minimal chrome).
                  It belongs in the identity menu, NOT the galaxy nav: it's a
                  property of YOU, not of any galaxy in scope. The link is a
                  fixed /me/* path — no :account — because the data is JWT-keyed
                  (/me/* APIs), so the Galaxy Boundary holds at the data layer and
                  the URL no longer pretends a galaxy frames it. */}
              <Link
                to="/me/career"
                onClick={() => setAccountMenuOpen(false)}
                className="flex items-center gap-md px-md py-md text-sm transition-colors hover:glass-subtle"
                style={{ color: "var(--text-muted)" }}
              >
                <Orbit className="h-4 w-4 flex-shrink-0" />
                {t("nav.career")}
              </Link>
              <Link
                to={`/${currentAccount}/control-room`}
                onClick={() => setAccountMenuOpen(false)}
                className="flex items-center gap-md px-md py-md text-sm transition-colors hover:glass-subtle"
                style={{ color: "var(--text-muted)" }}
              >
                <SlidersHorizontal className="h-4 w-4 flex-shrink-0" />
                {t("nav.control-room")}
              </Link>
              <Link
                to={`/${currentAccount}/feedback`}
                onClick={() => setAccountMenuOpen(false)}
                className="flex items-center gap-md px-md py-md text-sm transition-colors hover:glass-subtle"
                style={{ color: "var(--text-muted)" }}
              >
                <MessageCircle className="h-4 w-4 flex-shrink-0" />
                {t("nav.feedback")}
              </Link>
              {/* Per-galaxy visibility ("自分の見え方") lives in each galaxy's own
                  settings — a member sets only the galaxy they belong to. There is
                  no cross-galaxy anonymization entry here: managing another galaxy's
                  visibility from the account menu (or from a different galaxy) reads
                  as configuring a galaxy you're not in, which is confusing. */}
              {/* OSS research is the opt-in to be NAMED in EIS public OSS
                  research — also a property of YOU, not of any galaxy. */}
              <Link
                to={`/${currentAccount}/oss-presence`}
                onClick={() => setAccountMenuOpen(false)}
                className="flex items-center gap-md px-md py-md text-sm transition-colors hover:glass-subtle"
                style={{ color: "var(--text-muted)" }}
              >
                <Globe className="h-4 w-4 flex-shrink-0" />
                {t("nav.oss-research")}
              </Link>
              <div style={{ borderTop: "1px solid var(--border)" }} />
              <button
                onClick={handleLogout}
                className="flex w-full items-center gap-md px-md py-md text-sm transition-colors hover:glass-subtle"
                style={{ color: "var(--text-muted)" }}
              >
                <LogOut className="h-4 w-4 flex-shrink-0" />
                {t("general.logout")}
              </button>
            </div>
          )}
        </div>
      )}
      <div className="flex items-center px-md py-sm">
        <LangTabs locale={locale} onChange={setLocale} />
      </div>
      <ThemeToggle />
    </div>
  );
}

// Sliding-pill lang toggle. The squish animation has to be applied by
// removing the class + forcing a reflow + re-adding it, otherwise the
// browser short-circuits same-direction repeat clicks into a no-op.
type LangTabsProps = {
  locale: "ja" | "en";
  onChange: (l: "ja" | "en") => void;
};

function LangTabs({ locale, onChange }: LangTabsProps) {
  const ref = useRef<HTMLDivElement>(null);

  const handleClick = (next: "ja" | "en") => {
    if (next === locale) return;
    const el = ref.current;
    if (el) {
      el.classList.remove("squish-r", "squish-l");
      // Reflow so the animation restarts when clicking the same
      // direction twice (library.orbitlens.io does the same trick).
      void el.offsetWidth;
      el.classList.add(next === "en" ? "squish-r" : "squish-l");
    }
    onChange(next);
  };

  return (
    <div ref={ref} className="lang-tabs" data-active={locale}>
      <button
        type="button"
        className="lang-tab"
        data-tab-lang="ja"
        onClick={() => handleClick("ja")}
        aria-label="日本語"
      >
        JP
      </button>
      <button
        type="button"
        className="lang-tab"
        data-tab-lang="en"
        onClick={() => handleClick("en")}
        aria-label="English"
      >
        EN
      </button>
      <span className="lang-pill" aria-hidden="true" />
    </div>
  );
}
